// Module 16(b) §6 — the feature's ONLY `appHttp` importer (contract §4 boundary rule). Nothing
// else under `features/data-export` may import `@/shared/http`.
//
// FOUR ENVELOPES ACROSS FIVE ENDPOINTS (BE-16-08). `/export-service/` predates the enveloping
// convention and answers a different shape per route, so `unwrapEnvelope` fits none of them:
// each read below has its OWN bespoke unwrapper, named and commented at its call site. v3 did the
// same destructuring inline in two SFCs (`DataExportCreate.vue:422/433`,
// `DataExportTable.vue:245/255`), which is why the shapes were never written down anywhere.
//
// Every request is feature-owned (`suppressGlobalErrorHandler`) — the create `403` is a LIMIT
// SIGNAL the flow turns into the upgrade modal (contract §12), and a global toast firing alongside
// it would be the double-render this flag exists to prevent.
import type { AxiosRequestConfig } from 'axios'

import type {
  ExportJobCreateAck,
  ExportJobDto,
  ExportJobPayload,
  ExportRemainingCountsDto,
  ExportTemplateCatalogDto,
  ExportTemplateDto,
} from '../models/exportJob.types'

import { appHttp, unwrap } from '@/shared/http'

type ExportJobHttp = Pick<typeof appHttp, 'get' | 'post' | 'delete'>

const FEATURE_OWNED_ERRORS: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/** A count the backend may answer as a number, a numeric string, or not at all. */
function count(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0
}

/**
 * ENVELOPE 1 — remaining counts: a BARE OBJECT whose two keys sit at the top level, with no
 * `data`/`message` wrapper at all (`DataExportCreate.vue:422-424`). Legacy read it as
 * `data?.remaining_one_time_export_limit || 0`; the `|| 0` is preserved here as a clamp, because a
 * negative or non-numeric remaining would drive the usage tiles past their denominator.
 */
function readRemainingCounts(body: unknown): ExportRemainingCountsDto {
  const record = asRecord(body)
  return {
    remaining_one_time_export_limit: count(record.remaining_one_time_export_limit),
    remaining_scheduled_export_limit: count(record.remaining_scheduled_export_limit),
  }
}

/**
 * ENVELOPE 2 — the template catalog: rows ride `message` (not `data`, not the body root) and
 * `breakdown_combinations` is their SIBLING, not a nested field (`DataExportCreate.vue:435-436`).
 * Reading `.data` here would return `undefined`; reading the root would drop the combos.
 */
function readTemplateCatalog(body: unknown): ExportTemplateCatalogDto {
  const record = asRecord(body)
  const templates = Array.isArray(record.message) ? (record.message as ExportTemplateDto[]) : []
  const combinations = Array.isArray(record.breakdown_combinations)
    ? (record.breakdown_combinations as string[][]).filter(Array.isArray)
    : []
  return { message: templates, breakdown_combinations: combinations }
}

/**
 * ENVELOPE 3 — the jobs list: a BARE ARRAY as the response body (`DataExportTable.vue:259-261`).
 * A non-array body (the shape an error page or a future envelope would produce) reads as empty
 * rather than throwing, which is what keeps a malformed 200 out of the table's row loop.
 */
function readJobs(body: unknown): ExportJobDto[] {
  return Array.isArray(body) ? (body as ExportJobDto[]) : []
}

/**
 * ENVELOPE 4 — the write acknowledgement: create/delete answer a loose `{status, message}` that
 * legacy never read (`DataExportCreate.vue:658`, `DataExportTable.vue:304`) and no published
 * schema describes. It is surfaced as a typed-but-open record so a future ratified body is an
 * additive change; failures never arrive here — they reject as `ApiError` (`data.errors` carries
 * the backend's own string-or-array detail, which `ApiError.detail` already joins).
 */
function readAck(body: unknown): ExportJobCreateAck {
  const record = asRecord(body)
  return {
    ...(typeof record.status === 'string' ? { status: record.status } : {}),
    ...('message' in record ? { message: record.message } : {}),
  }
}

export class ExportJobApi {
  constructor(private readonly http: ExportJobHttp = appHttp) {}

  /** `GET /export-service/get-remaining-export-job-counts/{accountId}` (envelope 1). */
  async fetchRemainingCounts(accountId: number | string): Promise<ExportRemainingCountsDto> {
    const response = await this.http.get<unknown>(
      `/export-service/get-remaining-export-job-counts/${accountId}`,
      FEATURE_OWNED_ERRORS,
    )
    return readRemainingCounts(unwrap(response))
  }

  /**
   * `GET /export-service/get-export-job-templates/?platforms={csv}` (envelope 2).
   *
   * The platform list goes through `params`, not a hand-built query string: legacy interpolated it
   * raw (`:433`), so a platform identifier needing escaping would have produced a malformed URL.
   */
  async fetchTemplates(platforms: readonly string[]): Promise<ExportTemplateCatalogDto> {
    const response = await this.http.get<unknown>('/export-service/get-export-job-templates/', {
      ...FEATURE_OWNED_ERRORS,
      params: { platforms: platforms.join(',') },
    })
    return readTemplateCatalog(unwrap(response))
  }

  /** `GET /export-service/get-export-jobs/{accountUserId}/{workspaceId}` (envelope 3). */
  async fetchJobs(
    accountUserId: number | string,
    workspaceId: number | string,
  ): Promise<ExportJobDto[]> {
    const response = await this.http.get<unknown>(
      `/export-service/get-export-jobs/${accountUserId}/${workspaceId}`,
      FEATURE_OWNED_ERRORS,
    )
    return readJobs(unwrap(response))
  }

  /**
   * `POST /export-service/create-export-job/{accountUserId}/{workspaceId}` (envelope 4).
   *
   * Rejects `ApiError{kind:'forbidden'}` on the server-side limit (403) — the flow turns that into
   * the upgrade modal, never a toast (contract §12/§14).
   *
   * NO `Idempotency-Key` is sent yet. ORP-20067 delivered a retry-safe create contract on the
   * backend and contract §6 requires one key per confirmed wizard submission, but the key must be
   * minted ONCE per submission and reused across retries — which means it belongs to the wizard's
   * submit action, the surface that owns "this is the same submission". Minting one here would
   * produce a fresh key on every retry and defeat the guarantee. The call site adopts it when the
   * wizard ticket lands; `create` takes it as an explicit argument then.
   */
  async create(
    accountUserId: number | string,
    workspaceId: number | string,
    payload: ExportJobPayload,
  ): Promise<ExportJobCreateAck> {
    const response = await this.http.post<unknown>(
      `/export-service/create-export-job/${accountUserId}/${workspaceId}`,
      payload,
      FEATURE_OWNED_ERRORS,
    )
    return readAck(unwrap(response))
  }

  /**
   * `DELETE /export-service/delete-export-jobs/{accountUserId}/{workspaceId}/{groupId}`
   * (envelope 4 — an acknowledgement body nobody reads).
   */
  async remove(
    accountUserId: number | string,
    workspaceId: number | string,
    groupId: string,
  ): Promise<void> {
    await this.http.delete(
      `/export-service/delete-export-jobs/${accountUserId}/${workspaceId}/${groupId}`,
      FEATURE_OWNED_ERRORS,
    )
  }
}

export const exportJobApi = new ExportJobApi()
